import { ApolloError, ApolloQueryResult, FetchPolicy, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';
import { notNil } from '../notNil';
import { useCurrentBusinessId } from './useCurrentBusinessId';

interface UseProfileProps {
  fetchPolicy?: FetchPolicy;
  skip?: boolean;
}

interface UseProfileResult {
  profile: API.ProfileFragment | undefined;
  businesses: API.BusinessFragment[] | undefined;
  refetch: () => Promise<ApolloQueryResult<API.ProfileQuery>>;
  error?: ApolloError;
  loading: boolean;
}

const defaultProps: UseProfileProps = {
  fetchPolicy: 'cache-first',
  skip: false,
};

export function useProfile({
  fetchPolicy,
  skip,
}: UseProfileProps = defaultProps): UseProfileResult {
  const { data, refetch, error, loading } = useQuery<API.ProfileQuery>(Queries.user.profile, {
    fetchPolicy,
    skip,
  });

  return useMemo(() => {
    return {
      profile: data?.me ?? undefined,
      businesses: data?.me?.businesses.filter(notNil) ?? undefined,
      refetch,
      error,
      loading,
    };
  }, [data, refetch, error, loading]);
}

export function useCurrentBusiness(): API.BusinessFragment | undefined {
  const businessId = useCurrentBusinessId();
  const { businesses } = useProfile();
  return businesses?.find(b => b.id === businessId);
}
